// Single source for "a user selection becomes a loaded document": extension
// allowlist, browser byte budget, worker decode, progress reporting, and
// stale-read protection. The two-pane comparison input (src/ui/input.js) and the
// standalone Parquet viewer page (src/parquet-viewer.js) both drive this, so
// neither can drift into its own upload rules (G51). No DOM here — callers get
// status callbacks and apply them to their own markup.
import { unsupportedUploadFormat, unsupportedUploadMessage } from './upload-formats.js';
import { checkInputBytes, InputLimitError } from './input-limits.js';
import { readInputFiles } from './file-reader.js';
import { discoverDrop, selectedFiles } from './file-discovery.js';

export const PARQUET_ONLY_MESSAGE =
  'This viewer opens .parquet files and folders of .parquet parts only. Use the comparison tool for text, JSON, YAML, CSV and TSV.';

const isParquet = (name) => /\.parquet$/i.test(String(name || ''));

/** The filename/dataset label a pane shows for a loaded selection. */
export function documentLabel({ document, entries, directory }) {
  if (document?.dataset) {
    const root = directory && entries[0] ? `${entries[0].path.split('/')[0]} · ` : '';
    return `${root}${document.files.length} Parquet files`;
  }
  return entries[0]?.file?.name || '';
}

/**
 * @param readBinary   worker-client readBinary (injected so tests and the
 *                     main-thread fallback use the same path).
 * @param otherBytes   bytes already committed by the *other* pane; the viewer
 *                     page passes nothing, so its budget is a single dataset.
 * @param onStatus     {state:'busy'|'error'|'idle', message, reason?} — never
 *                     content. `reason` is a BOUNDED code on errors ('format' |
 *                     'oversize' | 'other') so a caller can report why a selection
 *                     was refused without parsing, or transmitting, the message.
 * @param only         'parquet' restricts the loader to binary Parquet input.
 * @param singleBudget the caller holds ONE selection, so limit messages must
 *                     say "25 MB" rather than "25 MB per side".
 */
export function createDocumentLoader({
  readBinary,
  otherBytes = () => 0,
  onStatus = () => {},
  onUnsupported = () => {},
  only = null,
  singleBudget = false,
} = {}) {
  let revision = 0;
  let loading = false;

  // Anything that makes an in-flight read stale (clearing, a manual edit, a new
  // selection) bumps the revision; late resolutions are then dropped instead of
  // overwriting newer input.
  const invalidate = () => {
    loading = false;
    return ++revision;
  };

  async function load(files, { directory = false, entries = selectedFiles(files), revision: rev = ++revision } = {}) {
    if (rev !== revision) return null;
    loading = false;
    const file = entries[0]?.file;
    const single = !directory && entries.length === 1;
    if (only === 'parquet' && single && !isParquet(entries[0]?.path || file?.name)) {
      onStatus({ state: 'error', message: PARQUET_ONLY_MESSAGE, reason: 'format' });
      onUnsupported(unsupportedUploadFormat(file || {}) || 'other');
      return null;
    }
    const unsupported = single && unsupportedUploadFormat(file);
    if (unsupported) {
      onStatus({ state: 'error', message: unsupportedUploadMessage(unsupported), reason: 'format' });
      onUnsupported(unsupported);
      return null;
    }
    const other = otherBytes();
    // Folder selections are budgeted on their Parquet parts only — excluded
    // files are never read, so they must not consume the allowance.
    const bytes = entries
      .filter((entry) => !directory || isParquet(entry.path))
      .reduce((sum, entry) => sum + (entry.file.size || 0), 0);
    let document;
    loading = true;
    onStatus({ state: 'busy', message: directory || entries.length > 1 ? 'Validating Parquet dataset…' : 'Reading file…' });
    try {
      document = await readInputFiles(entries, readBinary, {
        directory,
        otherBytes: other,
        single: singleBudget,
        onProgress: (progress) => {
          if (rev === revision) onStatus({ state: 'busy', message: `${progress.stage} Parquet files: ${progress.completed} of ${progress.total}…` });
        },
      });
      if (rev !== revision) return null;
      // Re-check after the await: the other pane may have grown while this read
      // was in flight, and the text path only saw file.size.
      checkInputBytes(bytes, otherBytes(), { single: singleBudget });
    } catch (error) {
      if (rev !== revision) return null;
      // Keep the precise message for limits, datasets and Parquet (they name the
      // offending part); a generic read failure must not echo parser internals.
      onStatus({
        state: 'error',
        reason: error instanceof InputLimitError ? 'oversize' : 'other',
        message: error instanceof InputLimitError || directory || !single || isParquet(file?.name) ? error.message : 'Could not read this file. Please try again.',
      });
      return null;
    } finally {
      if (rev === revision) loading = false;
    }
    if (rev !== revision) return null;
    onStatus({ state: 'idle' });
    return { document, bytes, entries, directory, revision: rev };
  }

  async function loadDrop(dataTransfer) {
    const rev = ++revision;
    loading = true;
    onStatus({ state: 'busy', message: 'Discovering files…' });
    try {
      const selection = await discoverDrop(dataTransfer);
      if (rev !== revision) return null;
      return await load([], { ...selection, revision: rev });
    } catch (error) {
      if (rev === revision) {
        loading = false;
        onStatus({ state: 'error', message: error.message, reason: 'other' });
      }
      return null;
    }
  }

  return { load, loadDrop, invalidate, isLoading: () => loading };
}
